/**
 * Show styles for the views (D32): the list, one show style with its config,
 * create, save, delete, and the judgement of a show style against a studio
 * (`validateShowStyle`: the pair's problems and the layer table).
 * Query/mutation option factories over the API client (`studios.ts`'s pattern).
 *
 * **Optimistic concurrency** as for studios: a save made from a version
 * someone else has since replaced comes back as a `ShowStyleConflict`
 * carrying the show style as it now is.
 */
import { ApiError, type ApiClient } from '@acp/api-client/client.js';
import type { ShowStyleWire, ShowStyleSummaryWire, ValidatePairResponseWire } from '@acp/api-contract/showStyles.js';
import type { JsonValue } from '@acp/domain/primitives.js';
import { type QueryClient, queryOptions } from '@tanstack/react-query';
import { studioKeys } from './studios.js';

export const showStyleKeys = {
  all: ['show-styles'] as const,
  list: () => [...showStyleKeys.all, 'list'] as const,
  one: (showStyleId: string) => [...showStyleKeys.all, 'one', showStyleId] as const,
  validate: (showStyleId: string, studioId: string) => [...showStyleKeys.all, 'validate', showStyleId, studioId] as const,
};

/** Every show style of the tenant, by name. */
export function showStylesQuery(api: ApiClient) {
  return queryOptions({
    queryKey: showStyleKeys.list(),
    queryFn: async (): Promise<ShowStyleSummaryWire[]> => {
      const res = await api.call('listShowStyles', { params: null, query: null, body: null });
      return [...res.showStyles].sort((a, b) => a.name.localeCompare(b.name) || a.showStyleId.localeCompare(b.showStyleId));
    },
  });
}

export function showStyleQuery(api: ApiClient, showStyleId: string) {
  return queryOptions({
    queryKey: showStyleKeys.one(showStyleId),
    queryFn: async (): Promise<ShowStyleWire> => (await api.call('getShowStyle', { params: { showStyleId }, query: null, body: null })).showStyle,
  });
}

/** Would a production of this show style in this studio resolve? */
export function validateShowStyleQuery(api: ApiClient, showStyleId: string, studioId: string) {
  return queryOptions({
    queryKey: showStyleKeys.validate(showStyleId, studioId),
    queryFn: async (): Promise<ValidatePairResponseWire> => api.call('validateShowStyle', { params: { showStyleId }, query: { studioId }, body: null }),
  });
}

export type NewShowStyle = {
  /** Null: the server mints one. */
  readonly showStyleId: string | null;
  readonly name: string;
  readonly config: { readonly [key: string]: JsonValue };
};

export function createShowStyleMutation(api: ApiClient, queryClient: QueryClient) {
  return {
    mutationFn: async (input: NewShowStyle): Promise<ShowStyleWire> => {
      const body = { ...(input.showStyleId === null ? {} : { showStyleId: input.showStyleId }), name: input.name, config: { ...input.config } };
      return (await api.call('createShowStyle', { params: null, query: null, body })).showStyle;
    },
    onSuccess: async (showStyle: ShowStyleWire): Promise<void> => {
      queryClient.setQueryData(showStyleKeys.one(showStyle.showStyleId), showStyle);
      await queryClient.invalidateQueries({ queryKey: showStyleKeys.list() });
    },
  };
}

/** A save refused because the show style moved on: `current` is the show style as it now is. */
export class ShowStyleConflict extends Error {
  readonly current: ShowStyleWire;
  constructor(current: ShowStyleWire) {
    super(`conflict: ${current.updatedBy} saved version ${String(current.version)} meanwhile`);
    this.name = 'ShowStyleConflict';
    this.current = current;
  }
}

export type ShowStyleSave = {
  readonly showStyleId: string;
  /** The version the edit was made from. */
  readonly version: number;
  readonly name: string;
  readonly config: { readonly [key: string]: JsonValue };
};

export function saveShowStyleMutation(api: ApiClient, queryClient: QueryClient) {
  return {
    mutationFn: async (save: ShowStyleSave): Promise<ShowStyleWire> => {
      try {
        const body = { version: save.version, name: save.name, config: { ...save.config } };
        return (await api.call('updateShowStyle', { params: { showStyleId: save.showStyleId }, query: null, body })).showStyle;
      } catch (err) {
        if (!(err instanceof ApiError) || err.code !== 'conflict') throw err;
        throw new ShowStyleConflict((await api.call('getShowStyle', { params: { showStyleId: save.showStyleId }, query: null, body: null })).showStyle);
      }
    },
    onSuccess: async (showStyle: ShowStyleWire): Promise<void> => {
      queryClient.setQueryData(showStyleKeys.one(showStyle.showStyleId), showStyle);
      await queryClient.invalidateQueries({ queryKey: showStyleKeys.list() });
      // A studio's mapping table judged against the old version is stale.
      await queryClient.invalidateQueries({ queryKey: [...showStyleKeys.all, 'validate', showStyle.showStyleId] });
    },
  };
}

/**
 * Delete a show style (at the version shown). The server refuses one that is
 * a studio's default (`conflict`, naming the studios) — the error's message
 * says which, and the view shows it.
 */
export function deleteShowStyleMutation(api: ApiClient, queryClient: QueryClient) {
  return {
    mutationFn: async (input: { readonly showStyleId: string; readonly version: number }): Promise<ShowStyleWire> =>
      (await api.call('deleteShowStyle', { params: { showStyleId: input.showStyleId }, query: { version: input.version }, body: null })).showStyle,
    onSuccess: async (showStyle: ShowStyleWire): Promise<void> => {
      queryClient.removeQueries({ queryKey: showStyleKeys.one(showStyle.showStyleId) });
      await queryClient.invalidateQueries({ queryKey: showStyleKeys.list() });
      await queryClient.invalidateQueries({ queryKey: studioKeys.list() });
    },
  };
}
